import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {EMPTY, Observable} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {USER_TYPES} from '../AppConstants/UserConstants';
import {UserLogged} from '../payloads/UserLogged';
import {ResponseTemplate} from '../payloads/response-template';
import {IAM_MENU_ACTIONS, RESOURCE_PERMISSIONS} from '../shared/resources/permissions';
import {AppStateService} from './appstate/app.state.service';
import {LocalStorage} from './appstate/storage/local-storage';
import {ConstantService} from './constant.service';
import {NgxPermissionsService} from 'ngx-permissions';
import {isAdminPortalUserType} from './admin-portal-user.util';
import {extractAllowActions} from '../util/iam-policy.util';
import {setLastNavPermissionJson} from '../layout/nav-permission-sync.state';

const SMARTDINING_IAM_PREFIX = 'smartdining:';

/** Stable enum value list — legacy {@link RESOURCE_PERMISSIONS} enum. */
const ALL_RESOURCE_PERMISSION_VALUES: string[] = Object.values(RESOURCE_PERMISSIONS) as string[];

/** Platform IAM menu actions (demo, org lifecycle, internal CRM) — separate from the legacy enum. */
const ALL_IAM_MENU_ACTION_VALUES: string[] = Object.values(IAM_MENU_ACTIONS);

/** Full super-admin grant: legacy resources + IAM menu actions. */
const SUPER_USER_BASE_PERMISSIONS: string[] = [
  ...ALL_RESOURCE_PERMISSION_VALUES,
  ...ALL_IAM_MENU_ACTION_VALUES,
];

/**
 * Adds de-prefixed variants so ngx-permissions matches both legacy {@code smartdining:…} policies
 * and canonical IAM leaf strings ({@code resource:verb}).
 */
export function expandPermissionVariants(permissions: string[] | null | undefined): string[] {
  const out = new Set<string>();
  for (const raw of permissions ?? []) {
    if (typeof raw !== 'string') {
      continue;
    }
    const p = raw.trim();
    if (!p.length) {
      continue;
    }
    out.add(p);
    if (p.startsWith(SMARTDINING_IAM_PREFIX)) {
      out.add(p.slice(SMARTDINING_IAM_PREFIX.length));
    }
  }
  return [...out];
}

/**
 * Precomputed expanded IAM keys for super-user (hundreds of permissions × prefix variants).
 * Avoids re-running {@link expandPermissionVariants} on every shell init / login.
 */
const SUPER_USER_EXPANDED_PERMISSIONS: string[] = expandPermissionVariants(SUPER_USER_BASE_PERMISSIONS);

/**
 * Single place for admin-dashboard permission resolution:
 * - Super user: backend returns [] from `getUserPermissions`; UI grants full `RESOURCE_PERMISSIONS`
 *   (including platform bulk export; server still enforces IAM when `platformRoleId` is set on the user).
 * - Account manager: permission strings come from the API (IAM policy actions).
 * - Session restore (F5): rehydrate ngx-permissions from storage or refetch when cache is empty.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminPermissionBootstrapService {
  constructor(
    private readonly http: HttpClient,
    private readonly constant: ConstantService,
    private readonly appState: AppStateService,
    private readonly ngxPermissions: NgxPermissionsService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  private permissionsUrl(): string {
    return `${this.constant.API_ENDPOINT}roles/getUserPermissions`;
  }

  /** All menu / route permission keys for unrestricted super-admins (legacy + IAM menu actions). */
  allResourcePermissions(): string[] {
    return SUPER_USER_BASE_PERMISSIONS;
  }

  /**
   * Coerce API `data` to a clean string[] (handles null, nested arrays, or single string).
   */
  normalizeApiPermissionList(raw: unknown): string[] {
    if (typeof raw === 'string') {
      const t = raw.trim();
      return t ? [t] : [];
    }
    return extractAllowActions(raw);
  }

  /**
   * Merge API response with user type rules.
   */
  resolveEffectivePermissions(user: UserLogged, apiData: unknown): string[] {
    if (user.userType === USER_TYPES.SUPER_USER) {
      return this.allResourcePermissions();
    }
    return this.normalizeApiPermissionList(apiData);
  }

  /** Persist and activate in ngx-permissions (and LocalStorage via AppState setter). */
  applyToSession(permissions: string[]): void {
    const effective =
      permissions === SUPER_USER_BASE_PERMISSIONS
        ? SUPER_USER_EXPANDED_PERMISSIONS
        : expandPermissionVariants(permissions);
    this.appState.loadPermissions = effective;
    try {
      this.ngxPermissions.flushPermissions();
      this.ngxPermissions.loadPermissions(effective);
    } catch (e) {
      console.error('ngx-permissions apply failed', e);
    }
    /** Keeps {@link AdminSketchLayoutComponent.syncPermissionsIfNeeded} from flushing/loading again with the same list. */
    setLastNavPermissionJson(JSON.stringify(effective));
    if (isPlatformBrowser(this.platformId)) {
      this.document.defaultView?.dispatchEvent(new CustomEvent('sd-admin-permissions-applied'));
    }
  }

  /**
   * Login / post-auth: apply permissions synchronously so navigation is not blocked by
   * GET /roles/getUserPermissions (that call can hang while other auth endpoints succeed).
   * - Super-user: full UI permission set immediately.
   * - Account manager: restore from storage or empty, then refetch in background.
   */
  primeSessionPermissionsForLogin(user: UserLogged): void {
    if (user.userType === USER_TYPES.SUPER_USER) {
      this.applyToSession(this.allResourcePermissions());
      return;
    }
    const cached = LocalStorage.getPermissions();
    if (cached?.length) {
      this.applyToSession(cached);
    } else {
      this.applyToSession([]);
    }
    this.refetchPermissionsQuietly(user);
  }

  /**
   * GET /api/v1/roles/getUserPermissions then apply. Errors propagate (login flow shows toast).
   */
  loadPermissionsAfterLogin(user: UserLogged): Observable<string[]> {
    return this.http.get<ResponseTemplate<unknown>>(this.permissionsUrl()).pipe(
      map((res) => this.resolveEffectivePermissions(user, res?.data)),
      tap((perms) => this.applyToSession(perms)),
    );
  }

  /** Best-effort refetch when shell loads with empty cache; does not clear permissions on failure. */
  refetchPermissionsQuietly(user: UserLogged): void {
    this.http
      .get<ResponseTemplate<unknown>>(this.permissionsUrl())
      .pipe(
        map((res) => this.resolveEffectivePermissions(user, res?.data)),
        tap((perms) => this.applyToSession(perms)),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  /**
   * Early boot: valid JWT + stored profile → ngx-permissions matches storage without waiting for shell.
   */
  hydrateFromCachedSession(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!this.appState.user.isUserAuthenticated) {
      return;
    }
    const details = this.appState.user.userDetails;
    if (!details || !isAdminPortalUserType(details.userType)) {
      return;
    }

    if (details.userType === USER_TYPES.SUPER_USER) {
      this.applyToSession(this.allResourcePermissions());
      return;
    }

    const cached = LocalStorage.getPermissions();
    if (cached?.length) {
      this.applyToSession(cached);
    }
  }

  /**
   * When the main admin shell loads: guarantee super-user has full keys; refetch if account manager has no cache.
   */
  ensureHydratedForShell(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!this.appState.user.isUserAuthenticated) {
      return;
    }
    const u = this.appState.user.userDetails;
    if (!u || !isAdminPortalUserType(u.userType)) {
      return;
    }

    if (u.userType === USER_TYPES.SUPER_USER) {
      this.applyToSession(this.allResourcePermissions());
      return;
    }

    const cached = this.appState.permissions ?? [];
    if (cached.length > 0) {
      this.applyToSession(cached);
      return;
    }

    this.refetchPermissionsQuietly(u);
  }

  clearSessionPermissions(): void {
    this.ngxPermissions.flushPermissions();
  }
}
